
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { useBaseMfaState } from './useBaseMfaState';
import { useMfaFactors } from './useMfaFactors';
import { handleMfaError } from '../types';

/**
 * Hook for MFA enrollment verification operations
 * 
 * Provides functionality to verify and complete MFA enrollment
 * 
 * @returns MFA enrollment verification function
 */
export function useMfaEnrollmentVerification() {
  const { setIsLoading } = useBaseMfaState();
  const { fetchMfaFactors } = useMfaFactors();

  /**
   * Verify and complete MFA enrollment
   * 
   * This function:
   * 1. Validates the one-time password from the authenticator app
   * 2. Completes the enrollment if verification succeeds
   * 3. Updates the factor status in our database
   * 4. Updates the enrolled factors list on success
   * 5. Handles errors with user feedback
   * 
   * @param code - One-time password from authenticator app
   * @returns Promise resolving to boolean indicating success
   */
  const verifyMfaEnrollment = async (code: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User not authenticated');
      }
      
      // Get the current unverified factors
      const { data: factors, error: fetchError } = await supabase
        .from('mfa_factors')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'unverified')
        .limit(1);
      
      if (fetchError) throw fetchError;
      
      if (!factors || factors.length === 0) {
        throw new Error("No MFA factor found to verify");
      }
      
      const factorToVerify = factors[0];
      
      const { data, error } = await supabase.auth.mfa.challengeAndVerify({
        factorId: factorToVerify.id,
        code,
      });
      
      if (error) throw error;
      
      // Update the factor status in our database
      const { error: updateError } = await supabase
        .from('mfa_factors')
        .update({
          status: 'verified',
          verified_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', factorToVerify.id)
        .eq('user_id', user.id);
      
      if (updateError) {
        console.warn('Failed to update MFA factor status:', updateError);
      }
      
      // Update enrolled factors after successful enrollment
      await fetchMfaFactors();
      
      toast({
        title: "MFA Enabled",
        description: "Two-factor authentication has been successfully enabled.",
      });
      
      return true;
    } catch (error) {
      const mfaError = handleMfaError(error);
      console.error('MFA verification failed:', mfaError);
      toast({
        title: "MFA Verification Failed",
        description: mfaError.message || "Invalid code. Please try again.",
        variant: "destructive"
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    verifyMfaEnrollment
  };
}
